import Image from 'next/image';
import Link from 'next/link';
import GlassCard from './GlassCard';
import type { Soin } from '@/app/lib/soins';

interface Props {
  /** Soin ou atelier (même modèle), image déjà résolue côté serveur */
  item: Soin;
  href: string;
  ctaLabel: string;
}

export default function OfferCard({ item, href, ctaLabel }: Props) {
  return (
    // Toute la carte est cliquable ; le libellé s'anime au survol de la carte
    <Link
      href={href}
      className="block h-full rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-forest"
    >
      <GlassCard
        className={`relative overflow-hidden p-7 h-full flex flex-col gap-4 group ${
          item.image ? 'border-transparent' : ''
        }`}
      >
        {item.image && (
          <>
            <Image
              src={item.image}
              alt=""
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {/* Voile sombre pour garder le texte lisible sur la photo */}
            <div className="absolute inset-0 bg-gradient-to-t from-forest-darker/95 via-forest-darker/75 to-forest-darker/45" />
          </>
        )}
        <div className="relative">
          <h4
            className={`font-serif text-xl mb-2 ${
              item.image ? 'text-cream-50' : 'text-forest-dark'
            }`}
          >
            {item.name}
          </h4>
          <p
            className={`font-sans text-sm leading-relaxed ${
              item.image ? 'text-cream-100/85' : 'text-forest/65'
            }`}
          >
            {item.description}
          </p>
        </div>
        <div
          className={`relative flex items-center gap-3 font-sans text-xs ${
            item.image ? 'text-cream-100/70' : 'text-forest/50'
          }`}
        >
          <span>{item.duration}</span>
          <span>·</span>
          <span
            className={`font-medium ${
              item.image ? 'text-cream-50' : 'text-forest/70'
            }`}
          >
            {item.price}
          </span>
        </div>
        <div className="relative mt-auto pt-2">
          <span
            className={`font-sans text-xs tracking-wide transition-colors inline-flex items-center gap-1 ${
              item.image
                ? 'text-cream-100 group-hover:text-white'
                : 'text-forest-light group-hover:text-forest'
            }`}
          >
            {ctaLabel}
            <span aria-hidden="true" className="group-hover:translate-x-0.5 transition-transform">
              →
            </span>
          </span>
        </div>
      </GlassCard>
    </Link>
  );
}
